import { get, post } from "src/services/api";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ASSISTANT_ENVIRONMENT, ENGAGEMENT_TYPES, HYRO_ADMIN_PANEL_ACCOUNT_ID } from "@consts";
import { isPhoneAssistant } from "@utils";

const isProductionInboundVoiceAgent = ({ environment, channel, engagementType }) =>
  environment === ASSISTANT_ENVIRONMENT.PRODUCTION
  && isPhoneAssistant(channel)
  && engagementType !== ENGAGEMENT_TYPES.OUTBOUND;

export const accountAssistantsQuery = (accountId) => ({
  queryKey: [FETCH_QUERY_KEYS.ACCOUNT_ASSISTANTS, accountId],
  queryFn: async () => await post({ endpoint: "/accounts/findAccountAssistants", data: { accountId } }),
  staleTime: Infinity
});

export const prefetchAccountAssistants = (queryClient, accountId) => {
  if (!accountId || accountId === HYRO_ADMIN_PANEL_ACCOUNT_ID) return;
  queryClient.prefetchQuery(accountAssistantsQuery(accountId));
};

export const useFetchAccountAssistants = (accountId) => useQuery({
  ...accountAssistantsQuery(accountId),
  select: (data) => data.map((assistant) => ({
    ...assistant, id: assistant.assistantId, assistantName: assistant.name
  })),
  enabled: !!accountId && accountId !== HYRO_ADMIN_PANEL_ACCOUNT_ID
});

export const useHasMultipleOverviewAgents = (accountId) => {
  const { data: assistants = [], isLoading } = useFetchAccountAssistants(accountId);
  const eligibleAgentsCount = assistants.filter(isProductionInboundVoiceAgent).length;
  return { isLoading, hasMultipleOverviewAgents: eligibleAgentsCount > 1 };
};

export const useFetchDuplicateTargetAccounts = ({ enabled = true } = {}) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.DUPLICATE_TARGET_ACCOUNTS],
  queryFn: async () => await get({ endpoint: "/accounts/duplicate-target-accounts" }),
  select: (data) => data?.accounts ?? [],
  staleTime: 5 * 60 * 1000,
  enabled
});

export const useFetchDuplicateAgentNameAvailability = ({ name, enabled = true }) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.DUPLICATE_AGENT_NAME_AVAILABILITY, name],
  queryFn: async () => await get({ endpoint: "/accounts/duplicate-agent-name-availability", params: { name } }),
  select: (data) => data?.available ?? true,
  staleTime: 30 * 1000,
  enabled: enabled && Boolean(name)
});

export const useFetchAccountDetails = (accountId) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.ACCOUNT_DETAILS, accountId],
  queryFn: async ({ signal }) => await get({ endpoint: `accounts/${accountId}/details`, signal }),
  staleTime: Infinity,
  enabled: !!accountId && accountId !== HYRO_ADMIN_PANEL_ACCOUNT_ID
});

export const useGetAccessForAccount = (options = {}) => useMutation({
  mutationFn: async ({ accountId, reason, context }) =>
    await post({ endpoint: "/accounts/temporary-access", data: { accountId, reason, context } }),
  ...options
});
